import React, { useState, useCallback } from "react";
import styles from "./Step6_Checkout.module.scss";
import InputField from "../ui/InputField/InputField";
import Button from "../ui/Button/Button";
import LiqPayForm from "../ui/LiqPayForm/LiqPayForm";
import Spinner from "../ui/Spinner/Spinner";
import InfoBox from "../ui/InfoBox/InfoBox";
import { createLead, initiatePayment } from "../../api";
import { trackEvent } from "../../utils/analytics";
import { LEAD_STATUSES, FINAL_STEP_TYPES, SERVICE_DETAILS } from "../../constants";

// Basic validation regex
const emailRegex = /^\S+@\S+\.\S+$/;
const phoneRegex = /^\+?[1-9]\d{1,14}$/;

const Step6Checkout = ({ onNextStep, quizData, calculationResult }) => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
  });
  const [paymentData, setPaymentData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const isFormValid = 
    formData.name &&
    phoneRegex.test(formData.phone) &&
    emailRegex.test(formData.email);

  const handleCreateLead = useCallback(async (statusId) => {
    const leadPayload = {
      ...quizData,
      ...formData,
      totalPrice: calculationResult.totalPrice,
      prepayment: calculationResult.prepayment,
      statusId,
    };
    return await createLead(leadPayload);
  }, [quizData, formData, calculationResult]);

  const handlePayment = async () => {
    if (!isFormValid) return;
    setIsLoading(true);
    setError(null);

    try {
      trackEvent('begin_checkout', {
        currency: 'UAH',
        value: calculationResult.prepayment,
        items: [
          {
            item_id: quizData.package_type,
            item_name: SERVICE_DETAILS[quizData.package_type]?.packageName || quizData.package_type,
            price: calculationResult.prepayment
          }
        ],
        trademark_name: quizData.trademarkName
      });
      const leadResponse = await handleCreateLead(LEAD_STATUSES.NEW_LEAD);
      // Fire Meta Pixel Lead event on successful lead creation
      if (typeof window.fbq !== 'undefined') { window.fbq('track', 'Lead'); }
      console.log("Lead created:", leadResponse);

      const orderId = leadResponse.salesDriveData?.data?.orderId;
      if (!orderId) {
        throw new Error("Order ID was not generated. Please contact support.");
      }

      const paymentParams = {
        amount: calculationResult.prepayment,
        description: `Оплата послуг з реєстрації ТМ. Замовлення #${orderId}`,
        order_id: orderId,
      };

      const paymentResponse = await initiatePayment(paymentParams);
      if (paymentResponse.success) {
        setPaymentData(paymentResponse.paymentData);
      } else {
        throw new Error(paymentResponse.message || "Payment initiation failed");
      }
    } catch (err) {
      setError(err.message);
      console.error("Operation failed:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConsultation = async () => {
    if (!isFormValid) return;
    setIsLoading(true);
    setError(null);

    try {
      trackEvent('generate_lead', {
        currency: 'UAH',
        value: calculationResult.totalPrice,
        trademark_name: quizData.trademarkName,
        package_type: quizData.package_type
      });
      const data = await handleCreateLead(LEAD_STATUSES.CONSULTATION);
      if (data.success) {
        onNextStep({ finalStep: FINAL_STEP_TYPES.CONSULTATION });
      } else {
        throw new Error(data.message || "Lead creation failed");
      }
    } catch (err) {
      setError(err.message);
      console.error("Lead creation for consultation failed:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const packageName = SERVICE_DETAILS[quizData.package_type]?.packageName || 'N/A';
  const prepayment = calculationResult?.prepayment || 0;

  if (paymentData) {
    return <LiqPayForm data={paymentData.data} signature={paymentData.signature} />;
  }

  return (
    <div className={styles.checkoutContainer}>
      <p className={styles.subtitle}>Залишився останній крок, щоб запустити процес реєстрації вашого бренду.</p>
      <div className={styles.summary}>
        <h2>Ваше замовлення</h2>
        <div className={styles.summaryDetails}>
          <div className={styles.summaryItem}>
            <span className={styles.label}>Назва ТМ:</span>
            <span className={styles.value}>{quizData.trademarkName || "N/A"}</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.label}>Пакет послуг:</span>
            <span className={styles.value}>{packageName}</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.label}>Юридична перевірка:</span>
            <span className={styles.value}>
              {quizData.needsSearch !== false ? "Так (+3 000 грн)" : "Ні (без перевірки)"}
            </span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.label}>Загальна вартість "під ключ":</span>
            <span className={`${styles.value} ${styles.totalPrice}`}>
              {calculationResult?.totalPrice ? `${calculationResult.totalPrice.toLocaleString('uk-UA')} грн` : "N/A"}
            </span>
          </div>
        </div>
      </div>

      <div className={styles.contactForm}>
        <h2>Контактні дані</h2>
        <p>Введіть ваші контактні дані для завершення</p>
        <InputField label="Ім'я" name="name" value={formData.name} onChange={handleChange} />
        <InputField label="Телефон" name="phone" type="tel" value={formData.phone} onChange={handleChange} placeholder="+380" />
        <InputField label="Email" name="email" type="email" value={formData.email} onChange={handleChange} />
      </div>

      {isLoading && <Spinner />}
      {error && <InfoBox message={error} type="error" />} 

      <div className={styles.actions}>
        <Button onClick={handlePayment} disabled={!isFormValid || isLoading} variant="primary">
          {isLoading ? 'Обробка...' : `Сплатити 1-й етап ${prepayment.toLocaleString('uk-UA')} грн`}
        </Button>
        <Button onClick={handleConsultation} disabled={!isFormValid || isLoading} variant="outline">
          {isLoading ? 'Обробка...' : 'Отримати консультацію перед оплатою'}
        </Button>
      </div>
    </div>
  );
};

export default Step6Checkout;
